import { DETAIL_ONLY_PARAMS, URLParameters } from '@/app/common/urlParameters.ts';

type CreateResultsParamsToDropProps = {
  highlightedHotel?: number;
  isNewSearch?: boolean;
  isChangeListView?: boolean;
  isAlternativeDates?: boolean;
  isBrowserBackNavigation?: boolean;
};

/**
 * The URL params a navigation to the results page must not carry over from the params it starts from.
 *
 * The highlight belongs to the navigation that set it: the error and back-to-results paths pass
 * highlightedHotel, and a new CSW search carries it inside newSearchParams (searching a hotel by name
 * travels as city + highlightHotelId). Every other navigation only inherits it from the live URL,
 * where filters, sort and paging rebuild their params from — keeping it would pin that hotel first
 * with its band and override the order just chosen. Kept for the navigations that are not a
 * refinement of the list: switching list/map is presentation, the alternative-dates chips follow the
 * very hotel that is highlighted (MPH-784), and a browser back restores a history entry whose URL is
 * the one the list was built with — rewriting it dropped the highlighted hotel from the first page
 * on the way back from its detail.
 */
export const createResultsParamsToDrop = ({
  highlightedHotel,
  isNewSearch = false,
  isChangeListView = false,
  isAlternativeDates = false,
  isBrowserBackNavigation = false,
}: CreateResultsParamsToDropProps): URLParameters[] => {
  const keepsHighlight =
    !!highlightedHotel || isNewSearch || isChangeListView || isAlternativeDates || isBrowserBackNavigation;

  return [
    // Shared with the back arrow's href so the two routes back to results cannot drift (MPH-850).
    ...DETAIL_ONLY_PARAMS,
    ...(isChangeListView ? [URLParameters.MAP_AREA] : []),
    ...(isAlternativeDates ? [URLParameters.FEED_CONTEXT] : []),
    ...(keepsHighlight ? [] : [URLParameters.HIGHLIGHT_HOTEL_ID]),
  ];
};
